// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { useIsStuck } from "@src/appV2/lib/utils/useIsStuck";
import { Button } from "@src/appV2/redesign/components/Button";
import {
  areCommaSeparatedSetsEqual,
  jobTypeOptions,
  shiftTypeOptions,
} from "@src/appV2/redesign/PlacementCandidate/constants";
import { CbhIcon, Text, ToggleButton } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { isEmpty, noop } from "lodash";
import { useMemo, useRef } from "react";

import { useGetWorkerOpenPlacementFilters } from "../../PlacementCandidate/api/useGetWorkerOpenPlacementFilters";
import { getDayPreferenceLabel } from "../../PlacementCandidate/utils";
import { useGetTags } from "../api/useGetTags";
import { useHasSeenPlacementFilterTooltip } from "../hooks/useHasSeenPlacementFilterTooltip";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { type Tag } from "../types/tag";
import { formatSelectedTagNamesById, hasChildcareQualification } from "../utils";
import { getFilterGroupDisplayName } from "../utils/filter-translation";
import { FilterButton } from "./FilterButton";
import { PlacementFilterTooltip } from "./Filters/Tooltip";

interface ComputedFilterSection {
  key: string;
  label: string;
  selected: boolean;
  labelNode?: React.ReactNode;
}

function computeShiftTypeSection(rawShiftTypes: string | undefined) {
  const defaultShiftTypes = shiftTypeOptions.map((o) => o.value).join(",");
  let label = "Shift times";
  let selected = false;
  let labelNode: React.ReactNode | undefined;

  if (
    rawShiftTypes &&
    rawShiftTypes.length > 0 &&
    !areCommaSeparatedSetsEqual(rawShiftTypes, defaultShiftTypes)
  ) {
    const shiftTypeValues = rawShiftTypes.split(",").filter(Boolean);
    const items = shiftTypeValues
      .map((v) => shiftTypeOptions.find((o) => o.value === v))
      .filter(isDefined);

    const labels = items.map((item) => item.label);

    if (labels.length > 0) {
      label = labels.join(", ");
      selected = true;
    }

    labelNode = (
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="nowrap">
        {items.map((item, index) => (
          <Stack key={item.value} direction="row" alignItems="center" spacing={1}>
            <CbhIcon type={item.icon} size="small" />
            <Text variant="body2">
              {item.label}
              {index < items.length - 1 && ","}
            </Text>
          </Stack>
        ))}
      </Stack>
    );
  }

  return { label, selected, labelNode };
}

function computeJobTypesSection(rawJobTypes: string | undefined): ComputedFilterSection {
  const defaultJobTypes = jobTypeOptions.map((o) => o.value).join(",");
  let label = "Job types";
  let selected = false;

  if (
    rawJobTypes &&
    rawJobTypes.length > 0 &&
    !areCommaSeparatedSetsEqual(rawJobTypes, defaultJobTypes)
  ) {
    const jobTypeLabels = rawJobTypes
      .split(",")
      .map((v) => jobTypeOptions.find((o) => o.value === v)?.label)
      .filter(isDefined);

    if (jobTypeLabels.length > 0) {
      label = jobTypeLabels.join(", ");
      selected = true;
    }
  }

  return { key: "jobTypes", label, selected };
}

function computeDaysOfWeekSection(rawDaysOfWeek: string | undefined): ComputedFilterSection {
  const defaultDaysOfWeek = "MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY,SATURDAY,SUNDAY";
  let label = "Days of the week";
  let selected = false;

  if (rawDaysOfWeek && !areCommaSeparatedSetsEqual(rawDaysOfWeek, defaultDaysOfWeek)) {
    label = getDayPreferenceLabel(rawDaysOfWeek.split(",") as DayOfTheWeek[]) ?? "";
    selected = true;
  }

  return { key: "daysOfWeek", label, selected };
}

function computeDistanceSection(rawDistanceLt: number | undefined): ComputedFilterSection {
  let label = "Distance";
  let selected = false;

  if (isDefined(rawDistanceLt) && rawDistanceLt > 0) {
    label = formatDistanceInMiles(rawDistanceLt, "long");
    selected = true;
  }

  return { key: "distanceInMiles", label, selected };
}

function computeAgeGroupsSection(
  rawAgeGroups: string | undefined,
  ageGroupTags: Tag[]
): ComputedFilterSection | undefined {
  if (isEmpty(ageGroupTags)) {
    return undefined;
  }

  let label = getFilterGroupDisplayName("ageGroups");
  let selected = false;

  if (rawAgeGroups && !isEmpty(rawAgeGroups)) {
    const selectedAgeGroupIds = rawAgeGroups.split(",").filter(Boolean);
    const selectedAgeGroupLabels = formatSelectedTagNamesById(selectedAgeGroupIds, ageGroupTags);

    if (!isEmpty(selectedAgeGroupLabels)) {
      label = selectedAgeGroupLabels;
      selected = true;
    }
  }

  return { key: "ageGroups", label, selected };
}

function computeWorkerTypesSection(
  rawWorkerTypes: string | undefined,
  actualWorkerTypes: string[],
  showWorkerTypes: boolean
): ComputedFilterSection | undefined {
  if (!showWorkerTypes) {
    return undefined;
  }

  let label = "Licenses";
  let selected = false;
  const defaultWorkerTypes = actualWorkerTypes.join(",");

  if (
    rawWorkerTypes &&
    rawWorkerTypes.length > 0 &&
    !areCommaSeparatedSetsEqual(rawWorkerTypes, defaultWorkerTypes)
  ) {
    const licenseLabels = rawWorkerTypes.split(",").filter(isDefined);

    if (licenseLabels.length > 1) {
      label = `${licenseLabels[0]} + ${licenseLabels.length - 1}`;
      selected = true;
    } else if (licenseLabels.length === 1) {
      // eslint-disable-next-line prefer-destructuring
      label = licenseLabels[0];
      selected = true;
    }
  }

  return { key: "workerTypes", label, selected };
}

function computeStateSection(
  rawState: string | undefined,
  availableStates: Array<{ code: string; name: string }>,
  shouldShowStateFilter: boolean
): ComputedFilterSection | undefined {
  if (!shouldShowStateFilter || availableStates.length <= 1) {
    return undefined;
  }

  let label = "State";
  let selected = false;
  const defaultStates = availableStates.map((s) => s.code).join(",");

  if (rawState && rawState.length > 0 && !areCommaSeparatedSetsEqual(rawState, defaultStates)) {
    const stateCodes = rawState.split(",").filter(isDefined);

    if (stateCodes.length > 1) {
      // Show first state name + count of additional states
      const firstName =
        availableStates.find((s) => s.code === stateCodes[0])?.name ?? stateCodes[0];
      label = `${firstName} + ${stateCodes.length - 1}`;
      selected = true;
    } else if (stateCodes.length === 1) {
      // Show single state name
      label = availableStates.find((s) => s.code === stateCodes[0])?.name ?? stateCodes[0];
      selected = true;
    }
  }

  return { key: "state", label, selected };
}

interface ComputeSectionsParams {
  filter: GetPlacementsForWorkplaceQuery["filter"] | undefined;
  actualWorkerTypes: string[] | undefined;
  showWorkerTypes: boolean;
  ageGroupTags: Tag[];
  availableStates: Array<{ code: string; name: string }>;
  shouldShowStateFilter: boolean;
}

function computeSections(params: ComputeSectionsParams): ComputedFilterSection[] {
  const {
    filter,
    actualWorkerTypes,
    showWorkerTypes,
    ageGroupTags,
    availableStates,
    shouldShowStateFilter,
  } = params;
  // Shift types section
  const {
    label: shiftTypeLabel,
    selected: shiftTypesSelected,
    labelNode: shiftTypeLabelNode,
  } = computeShiftTypeSection(filter?.shiftTypes);

  const shiftTypesSection = {
    key: "shiftTypes",
    label: shiftTypeLabel,
    selected: shiftTypesSelected,
    labelNode: shiftTypeLabelNode,
  };

  // Build sections array
  const sections = [
    computeJobTypesSection(filter?.jobTypes),
    shiftTypesSection,
    computeDaysOfWeekSection(filter?.daysOfWeek),
    computeStateSection(filter?.state, availableStates, shouldShowStateFilter),
    computeDistanceSection(filter?.distanceInMiles?.$lt),
    computeAgeGroupsSection(filter?.tags, ageGroupTags),
    isDefined(actualWorkerTypes)
      ? computeWorkerTypesSection(filter?.workerTypes, actualWorkerTypes, showWorkerTypes)
      : undefined,
  ].filter(isDefined);

  return sections.sort((a, b) => {
    if (a.selected === b.selected) {
      return 0;
    }

    if (a.selected) {
      return -1;
    }

    return 1;
  });
}

interface FiltersTopBarProps {
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter?: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  worker: Worker;
  placementCandidateId: string;
  showBackground?: boolean;
  /**
   * List of available states with codes and names from the backend
   */
  availableStates?: Array<{ code: string; name: string }>;
  /**
   * Whether the state filter should be shown. Only true when placements exist in more than one state.
   */
  shouldShowStateFilter?: boolean;
}

export function FiltersTopBar(props: FiltersTopBarProps) {
  const {
    filter,
    setFilter = noop,
    worker,
    placementCandidateId,
    showBackground = true,
    availableStates = [] as Array<{ code: string; name: string }>,
    shouldShowStateFilter = false,
  } = props;

  const { hasSeenPlacementFilterTooltip, setHasSeenPlacementFilterTooltip } =
    useHasSeenPlacementFilterTooltip();

  const headerRef = useRef<HTMLDivElement>(null);
  const isStuck = useIsStuck(headerRef);

  const { data: workerTypes } = useGetWorkerOpenPlacementFilters({
    workerId: worker.userId,
    useHiringWorkerTypes: true,
  });

  const showWorkerTypes = isDefined(workerTypes) && workerTypes.length > 1;

  // Check if worker has childcare qualifications to enable age group tags
  const workerQualifications = worker.licensesData?.map((license) => license.qualification) ?? [];
  const isChildcareWorker = hasChildcareQualification(workerQualifications);

  const { data: tagsData } = useGetTags(
    {
      entityType: "placement",
    },
    {
      enabled: isChildcareWorker,
    }
  );

  // Filter tags to only include age group tags
  const ageGroupTags = useMemo(() => {
    if (!tagsData?.data) {
      return [];
    }

    return tagsData.data.filter((tag) => tag.attributes.metadata?.filterGroup === "ageGroup");
  }, [tagsData]);

  const sections = useMemo(
    () =>
      computeSections({
        filter,
        actualWorkerTypes: workerTypes,
        showWorkerTypes,
        ageGroupTags,
        availableStates,
        shouldShowStateFilter,
      }),
    [filter, workerTypes, showWorkerTypes, ageGroupTags, availableStates, shouldShowStateFilter]
  );

  return (
    <Box
      ref={headerRef}
      sx={(theme) => ({
        position: "sticky",
        top: "-1px",
        zIndex: 1,
        "::before": {
          content: '""',
          display: "block",
          backgroundColor: showBackground ? theme.palette.background.tertiary : "transparent",
          position: "absolute",
          top: 0,
          zIndex: -1,
          boxSizing: "content-box",
          height: "100%",
          paddingBottom: theme.spacing(isStuck ? 5 : 6),
          width: `calc(100% + ${theme.spacing(5)} + ${theme.spacing(5)})`,
          left: `-${theme.spacing(5)}`,
          right: `-${theme.spacing(5)}`,
          borderBottom:
            showBackground && isStuck ? `1px solid ${theme.palette.border?.primary}` : "none",
          transition: showBackground ? "border-bottom 0.3s ease-in, color 0.3s ease-in" : "none",
        },
      })}
    >
      <FilterButton
        filter={filter}
        setFilter={setFilter}
        worker={worker}
        placementCandidateId={placementCandidateId}
        availableStates={availableStates}
        shouldShowStateFilter={shouldShowStateFilter}
        // refactor to a regular inline component, once the revamp feature flag is removed
        renderTrigger={({ open, totalAppliedFilters }) => (
          <Box
            sx={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              px: 0,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            <Stack
              direction="row"
              gap={2}
              sx={{ minWidth: "max-content", pt: isStuck ? 5 : 1, pr: 1 }}
            >
              <PlacementFilterTooltip
                hasSeenPlacementFilterTooltip={hasSeenPlacementFilterTooltip}
                setHasSeenPlacementFilterTooltip={setHasSeenPlacementFilterTooltip}
              >
                <Button
                  variant="outlined"
                  size="small"
                  isSelected={totalAppliedFilters > 0}
                  sx={{
                    position: "relative",
                    px: 4,
                    py: 3,
                    "&.MuiButton-root": {
                      minWidth: "48px",
                      width: "48px",
                      boxShadow: (theme) =>
                        `inset 0 0 0 ${theme.borderWidth?.regular} ${
                          totalAppliedFilters > 0
                            ? theme.palette.primary.main
                            : theme.palette.border?.primary
                        }`,
                    },

                    "& > .MuiButton-startIcon": {
                      mx: 0,
                      "& > svg": {
                        width: 24,
                        height: 24,
                      },
                    },
                  }}
                  startIconType="slider-settings"
                  onClick={() => {
                    setHasSeenPlacementFilterTooltip();
                    open();
                  }}
                >
                  {totalAppliedFilters > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -2,
                        right: -2,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor: (theme) => theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Text
                        semibold
                        variant="caption"
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      >
                        {totalAppliedFilters}
                      </Text>
                    </Box>
                  )}
                </Button>
              </PlacementFilterTooltip>
              {sections.map((section) => (
                <ToggleButton
                  key={section.key}
                  value={section.key}
                  selected={section.selected}
                  iconType={section.selected ? "checkmark" : undefined}
                  size="small"
                  sx={{
                    padding: 3,
                    backgroundColor: (theme) => theme.palette.background.tertiary,
                    border: (theme) => `2px solid ${theme.palette.border?.dark}`,

                    "& > svg": {
                      marginRight: 0,
                    },
                  }}
                  onClick={() => {
                    setHasSeenPlacementFilterTooltip();
                    open(section.key);
                  }}
                >
                  <Text semibold variant="body2">
                    {section.labelNode ?? section.label}
                  </Text>
                </ToggleButton>
              ))}
            </Stack>
          </Box>
        )}
      />
    </Box>
  );
}
